import {trimValue, convertToButton} from '../utilities/utilities';
import WindowMetrics from '../utilities/window-metrics';
import Header from './header/header';

const settings = {
    classNames: {
        container: 'popup-card',
        visible: 'popup-card--visible',
        scrollable: 'popup-card--scroll',
    },

    // When becoming visible, if the popup is outside the top or
    // bottom of this margin within the viewport, we scroll to
    // bring the popup onscreen
    viewportMinimumMargin: 16,
};

/** HotSpot popups seen on various pages (different from "InfoBox") */
export default class PopupCard {
    constructor(container, callbacks = {}) {
        this.container = container;
        this.callbacks = callbacks;
        this.visible = false;
        this.scrollable = false;

        // Find the child containers
        this.scrollContainer = this.container.querySelector('div');
        this.contentContainer = this.scrollContainer.firstElementChild;

        // Setup close button
        this.closeButton = this.container.querySelector('h4 span');
        let closeButtonClickCallback;
        if (this.closeButton) {
            // Convert the <span> into a <button> - doing this to avoid needing to redo the HTML
            this.closeButton = convertToButton(this.closeButton);

            if (callbacks.onClose) {
                // Rely on the callback to close the popup
                closeButtonClickCallback = function(event) {
                    let keyboardEvent = false;
                    if (event) {
                        event.preventDefault();
                        event.stopPropagation();

                        keyboardEvent = event instanceof KeyboardEvent;
                    }

                    this.callbacks.onClose(keyboardEvent);
                }.bind(this);
            } else {
                // Close on our own since there's no callback provided
                closeButtonClickCallback = function(event) {
                    if (event) {
                        event.preventDefault();
                        event.stopPropagation();
                    }

                    this.hide();
                }.bind(this);
            }

            this.closeButton.addEventListener(
                'keydown',
                (event) => {
                    if (event.key != 'Enter') {
                        return;
                    }

                    closeButtonClickCallback(event);
                },
            );

            this.closeButton.addEventListener('click', closeButtonClickCallback);

            // This causes unintended clicks too easily
            // this.closeButton.addEventListener('touchstart', closeButtonClickCallback);
        }

        // While the card is open, we monitor whether or not we need
        // to enable scrolling depending on whether our inner content
        // fits within the available height
        this.isMobile = WindowMetrics.isMobile();
        this.windowResizeCallback = function(event) {
            // Check if we've transitioned between desktop and mobile
            if (WindowMetrics.isMobile() != this.isMobile) {
                // Just close the popup when this occurs for simplicity
                closeButtonClickCallback();
                return;
            }

            // Everything is still either desktop or mobile - adjust
            // whether scroll is enabled
            checkScrollability.call(this);
        }.bind(this);
    }

    static get classNames() {
        return settings.classNames;
    }

    show(keyboardEvent) {
        if (this.visible) {
            return;
        }

        this.visible = true;

        // Stop any ongoing animation
        if (this.timeline) {
            this.timeline.progress(1);
        }

        this.container.classList.add(settings.classNames.visible);

        // Set this initial condition since there'll generally be a brief
        // delay before the first tick of the timeline
        this.container.style.opacity = 0;

        // Adjust whether scroll is enabled
        checkScrollability.call(this);

        // Update our flag indicating whether we opened in mobile or desktop
        this.isMobile = WindowMetrics.isMobile();

        // Start monitoring viewport resize events
        window.addEventListener('resize', this.windowResizeCallback);

        // Scroll into view, if necessary
        scrollIntoView.call(this);

        // Set input focus for keyboard navigation
        if (keyboardEvent === true) {
            this.closeButton.focus();
        }

        // Transforms are complicated because these already have something
        // applied in the CSS, which confuses GSAP
        const progress = {
            percentComplete: 0,
        };

        this.timeline = gsap.timeline();
        this.timeline.to(
            progress,
            0.5,
            {
                ease: 'power3.out',
                percentComplete: 1,
                onUpdate: function() {
                    this.container.style.opacity = trimValue(
                        progress.percentComplete,
                    );

                    const offset = trimValue(
                        16 * (1 - progress.percentComplete),
                    );

                    // The layout changes for these at 960px, not 768
                    if (
                        WindowMetrics.currentBreakpoint <
                        WindowMetrics.breakpoints.large
                    ) {
                        this.container.style.transform = `translate(-50%, ${offset}px)`;
                    } else {
                        this.container.style.transform = `translate(0, ${offset}px)`;
                    }
                }.bind(this),
            },
            0,
        );

        this.timeline.add(
            function() {
                this.timeline = null;
                this.container.removeAttribute('style');
            }.bind(this),
        );
    }

    hide() {
        if (!this.visible) {
            return;
        }

        this.visible = false;

        // Stop any ongoing animation
        if (this.timeline) {
            this.timeline.progress(1);
        }

        const progress = {
            percentComplete: 0,
        };

        this.timeline = gsap.timeline();
        this.timeline.to(
            progress,
            0.25,
            {
                ease: 'power2.out',
                percentComplete: 1,
                onUpdate: function() {
                    this.container.style.opacity = trimValue(
                        (1 - progress.percentComplete),
                    );

                    const scale = trimValue(
                        1 -
                        (
                            0.025 * progress.percentComplete
                        ),
                    );

                    // The layout changes for these at 960px, not 768
                    if (
                        WindowMetrics.currentBreakpoint <
                        WindowMetrics.breakpoints.large
                    ) {
                        this.container.style.transform = `translate(-50%, 0) scale(${scale})`;
                    } else {
                        this.container.style.transform = `scale(${scale})`;
                    }
                }.bind(this),
            },
            0,
        );

        this.timeline.add(
            function() {
                this.timeline = null;

                this.container.classList.remove(settings.classNames.visible);
                this.container.removeAttribute('style');

                setScrollability.call(this, false);

                // Stop monitoring viewport resize events
                window.removeEventListener('resize', this.windowResizeCallback);
            }.bind(this),
        );
    }
}

/*
    Internal functions
*/

/**
 * Scrolls the window if part of the popup is offscreen
 * @this PopupCard
 */
function scrollIntoView() {
    if (!this.visible) {
        return;
    }

    let scrollPosition = null;
    const bounds = this.container.getBoundingClientRect();
    if (bounds.top < settings.viewportMinimumMargin) {
        // Scroll upward
        const scrollDistance =
            bounds.top -
            settings.viewportMinimumMargin;

        scrollPosition =
            window.pageYOffset +
            scrollDistance;

        if (scrollPosition < 0) {
            scrollPosition = 0;
        }
    } else {
        const bottomViewportEdge =
            window.innerHeight -
            settings.viewportMinimumMargin;

        if (bounds.bottom > bottomViewportEdge) {
            // Scroll downward
            const scrollDistance =
                bounds.bottom -
                bottomViewportEdge;

            scrollPosition =
                window.pageYOffset +
                scrollDistance;

            // Double check that scrolling this distance won't
            // push the top of the popup offscreen
            const newTopPosition = bounds.top - scrollDistance;
            if (newTopPosition < settings.viewportMinimumMargin) {
                scrollPosition -=
                    settings.viewportMinimumMargin -
                    newTopPosition;
            }
        }
    }

    if (scrollPosition !== null) {
        // Tell the header to be hidden for a moment
        Header.suppressVisibility(1000);

        window.scrollTo({
            left: 0,
            top: Math.round(scrollPosition),

            // EDIT: I dunno if we want to lock scrolling... the interior
            // scrolling seems to work ok in iOS and I'm not sure it'd be
            // clear to users that they'd need to close any open popup
            // before continuing to scroll down the page
            // Note that we're doing this immediately since on mobile
            // we'll be locking scrolling right after this
            behavior: 'smooth',
            // behavior: 'auto',
        });
    }
}

/**
 * Changes our scrollable state
 * @this PopupCard
 * @param {boolean} scrollable
 */
function setScrollability(scrollable) {
    if (scrollable) {
        // Becoming scrollable
        if (!this.scrollable) {
            this.scrollable = true;
            this.container.classList.add(settings.classNames.scrollable);
            this.scrollContainer.scrollTo(0, 0);
        }
    } else {
        // Becoming unscrollable
        if (this.scrollable) {
            this.scrollable = false;
            this.container.classList.remove(settings.classNames.scrollable);
            this.scrollContainer.scrollTo(0, 0);
        }
    }
}

/**
 * Adjusts whether scrolling is enabled on the inner <div>
 * @this PopupCard
 */
function checkScrollability() {
    const availableHeight = this.scrollContainer.clientHeight;
    const requiredHeight = this.contentContainer.clientHeight;

    // The "+ 1" adds a little wiggle room for rounding errors
    if (requiredHeight < availableHeight + 1) {
        // We shouldn't be scrollable
        setScrollability.call(this, false);
    } else {
        // We should be scrollable
        setScrollability.call(this, true);
    }
}
